import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { deviceApi } from '../../api/deviceApi';
import { useSelector } from 'react-redux';
import { selectUserId } from '../Login/loginSlice';
import './DeviceLimitModal.css'


export default function DeviceLimitModal({ onClose = () => { }, onResolved = () => { }, inline = false }) {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);
    const userId = useSelector(selectUserId);

    useEffect(() => {
        // Load devices when component mounts (parent controls mounting conditionally)
        loadDevices();
    }, []);

    const loadDevices = async () => {
        try {
            const res = await deviceApi.getAll(userId);
            // Normalize possible response shapes: array, { devices: [...] }, { data: [...] }, or wrapped object
            // and normalize field names (snake_case vs camelCase)
            // Log for easier debugging (can be removed)
            // console.debug('deviceApi.getAll response:', res?.data);
            let list = [];
            if (Array.isArray(res.data)) list = res.data;
            else if (Array.isArray(res.data?.devices)) list = res.data.devices;
            else if (Array.isArray(res.data?.data)) list = res.data.data;
            else if (res.data && typeof res.data === 'object') {
                // Maybe server returns object with keys being devices
                // Try to find the first array value
                const firstArray = Object.values(res.data).find(v => Array.isArray(v));
                if (firstArray) list = firstArray;
            }

            // If server returned nested arrays ([[...],[...]]) flatten one level
            if (list.some && list.some(Array.isArray)) {
                list = list.flat();
            }

            // Normalize fields for each device
            const normalized = list.map(d => ({
                device_id: d?.device_id ?? d?.deviceId ?? d?.id ?? d?.device ?? null,
                type_device: d?.type_device ?? d?.typeDevice ?? d?.type ?? null,
                ip: d?.ip ?? d?.ip_address ?? d?.ipAddress ?? null,
                last_active_at: d?.last_active_at ?? d?.lastActiveAt ?? d?.last_active ?? null,
                raw: d,
            }));

            setDevices(normalized);
        } catch (err) {
            console.error('Failed to load devices', err);
            setDevices([]); // важно!
        }
    };

    const revokeDevice = async (deviceId) => {
        setLoading(true);
        await deviceApi.revokeOne(userId, deviceId);
        await loadDevices();
        setLoading(false);
    };

    const revokeAll = async () => {
        setLoading(true);
        await deviceApi.revokeAll(userId);
        setLoading(false);
        onResolved(); // закрываем и пробуем снова
    };

    const content = (
        <div className="modal-dialog  modal-dialog-centered" role="document">
            <div className="modal-content" style={{ borderLeft: '6px solid #565353' }}>
                <div className="modal-body p-4">
                    <h2 className="h4">Превышен лимит устройств</h2>
                    <p className="mb-3 text-muted">Выберите устройство для выхода</p>

                    {devices.length === 0 ? (
                        <div className="no-devices">Устройств не найдено</div>
                    ) : (
                        <ul className="list-unstyled mb-0">
                            {devices.map((d) => (
                                <li key={d.device_id || JSON.stringify(d.raw)} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                                    <div>
                                        <div className="fw-bold">{d.type_device || 'Устройство'}</div>
                                        <div className="small text-muted">{d.ip || 'IP неизвестен'} • {d.last_active_at ? new Date(d.last_active_at).toLocaleString() : 'дата неизвестна'}</div>
                                    </div>
                                    <div>
                                        <button className="btn btn-dark btn-sm" disabled={loading} onClick={() => revokeDevice(d.device_id)}>Выйти</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="modal-footer d-flex justify-content-between">
                    <button className="btn btn-danger" disabled={loading} onClick={revokeAll}>Выйти со всех устройств</button>
                    <button className="btn btn-dark" onClick={onClose}>Отмена</button>
                </div>
            </div>
        </div>
    );

    if (inline) {
        return <div className="device-panel">{content}</div>;
    }

    if (typeof document !== 'undefined' && document.body) {
        return createPortal(
            <div className="modal-backdrop" role="dialog" aria-modal="true">
                {content}
            </div>,
            document.body
        );
    }

    return (
        <div className="modal-backdrop">
            {content}
        </div>
    );
}
